import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { utils, writeFile } from "xlsx";

interface EstadoCredencialesChartProps {
  stackedBarData: any[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={styles.customTooltip}>
        <p style={styles.tooltipLabel}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color, margin: "4px 0" }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const EstadoCredencialesChart: React.FC<EstadoCredencialesChartProps> = ({
  stackedBarData,
}) => {
  const exportCredentials = () => {
    const ws = utils.aoa_to_sheet([]);

    const headers = [
      "Sucursal",
      "Con Credenciales",
      "Sin Credenciales",
      "Total",
    ];
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

    let rowIndex = 2;
    stackedBarData.forEach((item) => {
      const row = [
        item.name,
        item["Con Credenciales"],
        item["Sin Credenciales"],
        item["Con Credenciales"] + item["Sin Credenciales"],
      ];
      utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex++;
    });

    ws["!cols"] = [{ wch: 35 }, { wch: 20 }, { wch: 20 }, { wch: 15 }];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Credenciales");
    writeFile(wb, "Estado_Credenciales_por_Sucursal.xlsx");
  };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-chart-bar"
              style={styles.chartTitleIcon}
            ></i>
            Estado de Credenciales por Sucursal
          </h3>
          <button
            onClick={exportCredentials}
            style={styles.exportChartButton}
            title="Descargar Excel"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#16a34a";
              e.currentTarget.style.borderColor = "#16a34a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#22c55e";
              e.currentTarget.style.borderColor = "#22c55e";
            }}
          >
            <i
              className="fa-solid fa-file-arrow-down"
              style={styles.exportChartIcon}
            ></i>
            <span style={styles.exportChartText}>Excel</span>
          </button>
        </div>
      </div>
      <div style={styles.chartBody}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={stackedBarData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 11 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="Con Credenciales"
              stackId="a"
              fill="#50B7C0"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="Sin Credenciales"
              stackId="a"
              fill="#FF6B6B"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    flex: 1,
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "20px 28px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#667eea",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  exportChartIcon: {
    color: "#fff",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  exportChartText: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    margin: 0,
  },
  chartBody: {
    padding: "28px",
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    color: "#2d3748",
  },
};

export default EstadoCredencialesChart;

